import { useContext, useMemo, useState } from 'react';
import { Box, ButtonBase, makeStyles } from '@material-ui/core';
import _ from 'lodash';
import moment from 'moment';
import { AutoSizer, CellMeasurer, CellMeasurerCache, Column, Table } from 'react-virtualized';
import { classNames, conditional, getUserInfo } from '../../../../utils';
import useIpad from '../../../../utils/useIpad';
import { AdmissionContext } from '../..';
import { BarcodeIcon, HiddenBarcodeIcon } from '../../../../components/Icon';
import Row from './components/Row';
import BusStopUI from './components/BusStopUI';
import defaultHeaderRenderer from './components/headerRenderer';
import OtherInfo from './components/OtherInfo';

const BusStop = ({ data, handleScroll, detailOpen, sort, setSort, completed, setPageNo }) => {
  const { showBusStop } = useContext(AdmissionContext);
  const classes = useStyles();
  const userInfo = getUserInfo();
  const isIpad = useIpad();
  const [showBarcode, setShowBarcode] = useState(false);

  const columns = [
    {
      label: 'A.D.T',
      dataKey: 'adt',
      widthBasis: 0.07,
    },
    {
      label: 'WT.',
      dataKey: 'adt',
      widthBasis: 0.05,
    },
    {
      label: 'Specialty',
      dataKey: 'adt',
      widthBasis: 0.08,
    },
    {
      dataKey: 'adt',
      widthBasis: showBarcode ? 0.185 : 0.1796,
      headerRenderer: () => (
        <Box display="flex" alignItems="center">
          <Box mr="0.357rem">Patient/AED LOC.</Box>
          <ButtonBase className={classes.headerActionButton} onClick={() => setShowBarcode(!showBarcode)}>
            {showBarcode ? (
              <HiddenBarcodeIcon className={classes.headerActionIcon} />
            ) : (
              <BarcodeIcon className={classes.headerActionIcon} />
            )}
          </ButtonBase>
        </Box>
      ),
    },
    {
      label: 'Diagnosis',
      dataKey: 'adt',
      widthBasis: isIpad ? 0.08 : 0.18,
    },
    {
      label: 'Priority',
      dataKey: 'adt',
      widthBasis: 0.065,
    },
    {
      label: 'Ward',
      dataKey: 'adt',
      widthBasis: 0.11,
    },
    {
      label: 'Current Status',
      dataKey: 'adt',
      widthBasis: 0.1,
    },
    {
      label: 'Waiting For',
      dataKey: 'adt',
      widthBasis: 0.09,
    },
    {
      label: 'Remark',
      dataKey: 'adt',
      widthBasis: isIpad ? 0.08 : 0.11,
    },
    {
      label: '',
      dataKey: 'adt',
      widthBasis: isIpad ? 0.06 : 0.04,
    },
  ];

  const handleSort = (type) => () => {
    setSort((preSort) => {
      if (preSort.sortKey === type) {
        return {
          sortKey: type,
          sortOrder: preSort.sortOrder === 'asc' ? 'desc' : 'asc',
        };
      } else {
        return {
          sortKey: type,
          sortOrder: 'asc',
        };
      }
    });
    setPageNo(1);
  };

  const tableData = useMemo(() => {
    let tempData = _.map(
      _.filter(data, (item) => item.display || (item.busStopRow && showBusStop.show)),
      (item) => ({
        ...item,
        patient: {
          caseNo: item.caseNo,
          bedNo: item.owBedNo,
          chiName: item.chiName,
          engName: item.engName,
          gender: item.sex,
          age: conditional(item.dob, moment().diff(moment(item.dob, 'DD-MM-YYYY'), 'years'), ''),
        },
        hold: item?.status === 'Hold',
      })
    );

    return tempData;
  }, [data, userInfo, showBusStop.show]);

  // 创建一个 CellMeasurerCache，用于缓存测量结果
  const cache = new CellMeasurerCache({
    defaultHeight: 48, // 默认高度
    fixedWidth: true, // 如果表格的宽度是固定的，可以设置为 true
  });

  const cellRenderer = ({ columnIndex, rowIndex, parent }) => {
    const rowData = tableData[rowIndex];
    const busStop = columnIndex === 0 && rowData.busStopRow;
    const otherInfo = rowData.otherInfo;

    if (!cache.has(rowIndex, columnIndex)) {
      cache.clearAll(); // 清除缓存以避免其他测量结果干扰
      cache.has(rowIndex, columnIndex); // 手动执行测量
    }

    return (
      <CellMeasurer
        cache={cache}
        columnIndex={columnIndex}
        key={`${rowData?.eaId}-${rowIndex}-${columnIndex}${busStop ? 'isBusStop' : 'notBusStop'}${
          otherInfo ? 'isOther' : 'notOther'
        }`}
        parent={parent}
        rowIndex={rowIndex}
      >
        {({ measure }) => renderContent(rowIndex, columnIndex, measure)}
      </CellMeasurer>
    );
  };

  const renderContent = (rowIndex, columnIndex, measure) => {
    const rowData = tableData[rowIndex];
    const busStop = columnIndex === 0 && rowData.busStopRow;
    const otherInfo = rowData.otherInfo;
    const cellKey = `${rowData?.eaId}-${rowIndex}-${columnIndex}-${otherInfo ? 'isOther' : 'notOther'}`;

    return rowData.busStopRow ? (
      busStop ? (
        <div key={cellKey} onLoad={measure}>
          <BusStopUI data={rowData?.busStopInfo} hold={rowData?.hold} gender={rowData?.sex} />
        </div>
      ) : null
    ) : otherInfo ? (
      columnIndex === 0 ? (
        <div key={cellKey} onLoad={measure}>
          <OtherInfo rowData={rowData} />
        </div>
      ) : null
    ) : (
      <Box key={cellKey} onLoad={measure} p="0.5rem 0.5rem 0.5rem 0">
        <Row
          {...rowData}
          showDetail={tableData[rowIndex + 1]?.busStopRow && tableData[rowIndex + 1]?.display}
          columnIndex={columnIndex}
          measure={measure}
          hold={rowData?.hold}
          showBarcode={showBarcode}
        />
      </Box>
    );
  };

  const getRowClassName = ({ index }) => {
    const rowData = tableData[index];
    if (rowData?.busStopRow) {
      return '';
    } else if (rowData?.busStopRow === false) {
      return getClassNameByWaitTime(rowData);
    } else {
      return classes.flexContainer;
    }
  };

  const getClassNameByWaitTime = (rowData) => {
    const waitTime = rowData?.waitTime;
    const otherInfo = rowData?.otherInfo;

    if (waitTime <= 14400) {
      return classNames(otherInfo ? classes.otherInfo : classes.flexContainer, classes.borderRow1);
    } else if (waitTime <= 28800) {
      return classNames(otherInfo ? classes.otherInfo : classes.flexContainer, classes.borderRow2);
    } else if (waitTime <= 43200) {
      return classNames(otherInfo ? classes.otherInfo : classes.flexContainer, classes.borderRow3);
    } else {
      return classNames(otherInfo ? classes.otherInfo : classes.flexContainer, classes.borderRow4);
    }
  };

  return (
    <div style={{ flex: 1 }}>
      <AutoSizer>
        {({ height, width }) => (
          <Table
            // height={completed ? height - 35 : height * 0.95 - (detailOpen ? 50 : 0) - 35}
            height={height}
            width={width}
            rowHeight={cache.rowHeight}
            headerHeight={32}
            rowCount={tableData.length}
            rowGetter={({ index }) => tableData[index]}
            rowClassName={getRowClassName}
            onScroll={handleScroll}
            overscanRowCount={0} // 预渲染0行，避免卡顿
          >
            {columns.map(({ dataKey, label, widthBasis, headerRenderer }) => {
              return (
                <Column
                  key={label}
                  headerRenderer={headerRenderer || defaultHeaderRenderer(sort, handleSort)}
                  headerClassName={classes.tableHead}
                  cellRenderer={cellRenderer}
                  label={label}
                  dataKey={dataKey}
                  width={width * widthBasis}
                />
              );
            })}
          </Table>
        )}
      </AutoSizer>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  flexContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
  },
  borderRow1: {
    borderTop: 'solid 1px #AAAAAA',
    backgroundColor: 'white',
  },
  borderRow2: {
    borderTop: 'solid 1px #AAAAAA',
    backgroundColor: '#fff4cd',
  },
  borderRow3: {
    borderTop: 'solid 1px #AAAAAA',
    backgroundColor: '#feeceb',
  },
  borderRow4: {
    borderTop: 'solid 1px #AAAAAA',
    backgroundColor: 'rgba(240,240,240)',
  },
  tableHead: {
    color: '#538FFF',
    fontFamily: 'NotoSans-Bold',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    borderBottom: 'none !important',
    alignItems: 'center',
    display: 'flex',
    height: '100%',
  },
  headerActionButton: {
    width: '1.5rem',
    height: '1.5rem',
    borderRadius: '50%',
    flexShrink: 0,
  },
  headerActionIcon: {
    fontSize: '1.5rem',
  },
  otherInfo: {
    borderTop: 'unset !important',
  },
}));

export default BusStop;
